import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_one } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  Alert,
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import {
  Button,
  Dt,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

type Props = {
  object: User;
  setPremiumMeasuresWeightOpen: (b:boolean) => void;
}

// The widget body is a mode picker setting on the coach// (`login.settings.userdetails_widgets.settings.measures_small.mode`):
// - 'last_weight' (default): the client's latest weight — big number + date +
//   goal line, from custom_api_last_weight (the client dashboard's source).
// - 'graph': the CoachUserMeasures chart block — weight + goal lines with
//   30/60-day + month navigation, from custom_api_weight_stats (the exact
//   query the measures page uses, minus its delete-point dot click).
// Both bodies are consent-gated on consent_status.is_measure_weight (the
// measures page's gate). The old goto-measures button + blood-uploads
// sub-section were removed (goto_links covers navigation; blood lives on
// the blood page) — so no module gating remains in the widget either.
export const CoachUserWidgetsMeasuresSmall: FunctionComponent<Props> = ({
  object,
  setPremiumMeasuresWeightOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  // Graph-mode navigation state, mirroring CoachUserMeasures.
  const [statDay, setStatDay] = useState<string>(DateTime.now().toFormat("dd/LL/y"));
  const [statRange, setStatRange] = useState<number>(30);

  // Coach-level widget settings (the `mode` body picker, default last_weight).
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.measuresSmallValueKey];
  const mode = HelpersClientWidgets.getMeasuresSmallMode(cfg);

  // Bodies only render with the measures consent (the measures page's gate).
  const hasMeasureConsent = !!object?.consent_status?.is_measure_weight;

  // last_weight body: the client's latest weight entry (+ goal), from the
  // custom_api_last_weight custom action — the client dashboard's source.
  // Only fetched in last_weight mode.
  const {
    data: lastWeight,
  } = useQuery({
    queryKey: ["last_weight", object?.id],
    queryFn: () =>
      fetch_one<{custom_result: {goal: number, weight: number, date: string}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        {
          object: 'user',
          class_action: 'custom_api_last_weight',
          id: object?.id,
        },
        login
      ),
    select: (d) => {
      return d.custom_result;
    },
    enabled: mode === 'last_weight' && !!object?.id && hasMeasureConsent,
  });

  // graph body: the CoachUserMeasures stats query (day + range → weight/goal
  // points). Only fetched in graph mode.
  const {
    data: weightStats,
  } = useQuery({
    queryKey: ["weight_stats", object?.id, statDay, statRange],
    queryFn: () =>
      fetch_one<{custom_result: {data: {name: string, weight: number, goal: number}[]}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        {
          object: 'user',
          class_action: 'custom_api_weight_stats',
          id: object?.id,
          day: statDay,
          range: statRange
        },
        login
      ),
    select: (d) => {
      return d.custom_result.data;
    },
    enabled: mode === 'graph' && !!object?.id && hasMeasureConsent,
  });

  return <Paper sx={{padding: 2, marginTop: 1}}>
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.measures")}</Typography>
        <Divider />
      </Grid>
      {!!object.extra_data?.weight_trigger && <Grid item xs={12}>
        <Alert severity='info'>
          <Typography html>{t("coach.models.clients.is_weight_trigger_short")}</Typography>
        </Alert>
      </Grid>}

      {/* No measures consent: the measures page's no-access notice. */}
      {!hasMeasureConsent && <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography sx={{
          fontStyle: 'italic',
          fontSize: '0.8rem',
          color: theme.palette.warning.main
        }}>{t("coach.consents.no_access")}</Typography>
      </Grid>}

      {/* last_weight body: big weight + date + goal line. The goal line
          renders whenever the payload is loaded (the client dashboard shows
          it regardless of a weight entry); the number + date only when an
          entry exists. */}
      {hasMeasureConsent && mode === 'last_weight' && <Grid item xs={12} sx={{textAlign: 'center'}}>
        {!!lastWeight?.weight && <Typography sx={{fontSize: '3rem'}}>
          {lastWeight?.weight?.toLocaleString(undefined, {maximumFractionDigits: 1})}
        </Typography>}
        {!!lastWeight?.weight && !!lastWeight?.date && <Typography variant="caption" sx={{display: 'block', color: 'text.secondary', marginTop: -1}}>
          <Dt d={lastWeight?.date} f="DDD" />
        </Typography>}
        {((lastWeight?.goal || 0) > 0) && <Typography variant="body2" sx={{marginTop: !!lastWeight?.weight ? 1 : 0}}>
          {t("client.measures.weight.goal_weight")}: {lastWeight?.goal?.toLocaleString(undefined, {maximumFractionDigits: 1})} kg
        </Typography>}
        {!lastWeight?.weight && <Typography sx={{fontStyle: 'italic'}} html>
          {t("shared.terms.no_results", "Er zijn geen resultaten beschikbaar")}
        </Typography>}
      </Grid>}

      {/* graph body: the CoachUserMeasures chart block (label + chart + nav),
          minus the delete-point dot click. ResponsiveContainer instead of the
          page's fixed width so the chart fits the widget column. */}
      {hasMeasureConsent && mode === 'graph' && <Grid item xs={12}>
        {statDay === DateTime.now().toFormat("dd/LL/y") && <Box sx={{textAlign: 'center'}}>
          {statRange === 30 && <Typography sx={{textDecoration: 'underline'}} html>{t("shared.terms.last_30_days", "Laatste 30 dagen")}</Typography>}
          {statRange === 60 && <Typography sx={{textDecoration: 'underline'}} html>{t("shared.terms.last_60_days", "Laatste 60 dagen")}</Typography>}
        </Box>}
        {statDay !== DateTime.now().toFormat("dd/LL/y") && <Box sx={{textAlign: 'center'}}>
          <Typography sx={{textDecoration: 'underline'}}>{DateTime.fromFormat(statDay, "dd/LL/y").toFormat("LLLL y")}</Typography>
        </Box>}

        {(weightStats|| []).filter(ff => !!ff.weight).length < 1 && <Box sx={{textAlign: 'center'}}>
          <Typography sx={{fontStyle: 'italic'}} html>{t("shared.terms.no_results", "Er zijn geen resultaten beschikbaar")}</Typography>
        </Box>}
        {(weightStats|| []).filter(ff => !!ff.weight).length > 0 && <Box sx={{textAlign: 'center', width: '100%'}}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={weightStats}
              margin={{
                top: 5,
                right: 10,
                left: 0,
                bottom: 30,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-30} textAnchor="end" />
              <YAxis />
              <Tooltip />
              <Legend verticalAlign="top" />
              <Line type="monotone" connectNulls dataKey="weight" stroke={theme.palette.primary.dark} activeDot={{ r: 8 }} />
              <Line type="monotone" connectNulls dataKey="goal" stroke={theme.palette.primary.main} />
            </LineChart>
          </ResponsiveContainer>
        </Box>}
        <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', columnGap: 1, rowGap: 0.5, marginTop: 1}}>
          <Button
            fullwidth={false}
            size="small"
            contained
            onClick={(e) => {
              e.preventDefault();
              setStatDay(DateTime.fromFormat(statDay, "dd/LL/y").minus({months: 1}).toFormat("dd/LL/y"));
            }}
            id="stats_back"
            startIcon={<ChevronLeftIcon />}
            label={t("shared.terms.previous_month", "Vorige maand")}
            sx={{marginTop: 0}}
          />
          <Button
            fullwidth={false}
            size="small"
            disabled={statDay === DateTime.now().toFormat("dd/LL/y") && statRange === 30}
            onClick={(e) => {
              setStatRange(30);
              setStatDay(DateTime.now().toFormat("dd/LL/y"));
            }}
            id="stats_30"
            label={t("shared.terms.last_30_days", "Laatste 30 dagen")}
            sx={{marginTop: 0}}
          />
          <Button
            fullwidth={false}
            size="small"
            disabled={statDay === DateTime.now().toFormat("dd/LL/y") && statRange === 60}
            onClick={(e) => {
              setStatRange(60);
              setStatDay(DateTime.now().toFormat("dd/LL/y"));
            }}
            id="stats_60"
            label={t("shared.terms.last_60_days", "Laatste 60 dagen")}
            sx={{marginTop: 0}}
          />
          <Button
            fullwidth={false}
            size="small"
            disabled={statDay === DateTime.now().toFormat("dd/LL/y")}
            contained
            onClick={(e) => {
              e.preventDefault();
              setStatDay(DateTime.fromFormat(statDay, "dd/LL/y").plus({months: 1}).toFormat("dd/LL/y"));
            }}
            id="stats_forward"
            endIcon={<ChevronRightIcon />}
            label={t("shared.terms.next_month", "Volgende maand")}
            sx={{marginTop: 0}}
          />
        </Box>
      </Grid>}
    </Grid>
  </Paper>;
}
